import type { App } from '@grafikr/components';

import intersectionLoader from 'utils/loaders/intersection';
import onEvent from 'utils/loaders/on-event';
import onElementInteraction from 'utils/loaders/on-element-interaction';
import hasUrlParam from 'utils/loaders/has-url-param';

import navigation from 'layout/navigation';

export default (app: App) => {
  app.add({
    // Layout
    navigation,
    'navigation-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "layout-navigation-drawer" */ 'layout/navigation-drawer'),
    ],
    'search-overlay': [
      'focusin',
      () => import(/* webpackChunkName: "layout-search-overlay" */ 'layout/search'),
    ],

    // Components
    drawer: [
      [onEvent, onElementInteraction],
      () => import(/* webpackChunkName: "component-drawer" */ 'components/drawer'),
    ],
    'navigation-item': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-navigation-item" */ 'components/navigation-item'),
    ],
    'address-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-address-form" */ 'components/address-form'),
    ],
    'featured-articles': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-featured-articles" */ 'components/featured-articles'
        ),
    ],
    carousel: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-carousel" */ 'components/carousel'),
    ],
    'cart-count': [
      [onEvent, intersectionLoader],
      () => import(/* webpackChunkName: "component-cart-count" */ 'components/cart-count'),
    ],
    'cart-drawer': [
      [onEvent, onElementInteraction, hasUrlParam],
      () => import(/* webpackChunkName: "component-cart-drawer" */ 'components/cart-drawer'),
    ],
    copy: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-copy" */ 'components/copy'),
    ],
    hotspot: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-hotspot" */ 'components/hotspot'),
    ],
    'klaviyo-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-klaviyo-form" */ 'components/klaviyo-form'),
    ],
    'lazy-content': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-lazy-content" */ 'components/lazy-content'),
    ],
    'product-wishlist-icon': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-product-wishlist-icon" */ 'components/product-wishlist-icon'
        ),
    ],
    'recently-viewed-products': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-recently-viewed-products" */ 'components/recently-viewed-products'
        ),
    ],
    collapsible: [
      'click',
      () => import(/* webpackChunkName: "component-collapsible" */ 'components/collapsible'),
    ],
    'algolia-feed': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-algolia-feed" */ 'components/algolia-feed'),
    ],
    'bullet-list': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-bullet-list" */ 'components/bullet-list'),
    ],
    'announcement-bar': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-announcement-bar" */ 'components/announcement-bar'),
    ],
    countdown: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-countdown" */ 'components/countdown'),
    ],
    'simple-product-form': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "component-simple-product-form" */ 'components/simple-product-form'
        ),
    ],
    video: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-video" */ 'components/video'),
    ],
    locations: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-locations" */ 'components/locations'),
    ],
    'open-state': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-open-state" */ 'components/open-state'),
    ],
    'newsletter-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "component-newsletter-drawer" */ 'components/newsletter-drawer'
        ),
    ],
    focus: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-focus" */ 'components/focus'),
    ],
    wishlist: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-wishlist" */ 'components/wishlist'),
    ],
    'wishlist-count': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-wishlist-count" */ 'components/wishlist-count'),
    ],
    'wishlist-shared': [
      intersectionLoader,
      () =>
        import(/* webpackChunkName: "component-wishlist-shared" */ 'components/wishlist-shared'),
    ],
    toggle: [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-toggle" */ 'components/toggle'),
    ],
    'product-reviews': [
      intersectionLoader,
      () => import(/* webpackChunkName: "component-product-reviews" */ 'components/product-reviews'),
    ],

    // Pages
    cart: [intersectionLoader, () => import(/* webpackChunkName: "cart-main" */ 'pages/cart/main')],

    'customer-login': [
      intersectionLoader,
      () => import(/* webpackChunkName: "customer-login" */ 'pages/customer/login'),
    ],
    'customer-wishlist': [
      intersectionLoader,
      () => import(/* webpackChunkName: "customer-wishlist" */ 'pages/customer/wishlist'),
    ],
    'customer-orders-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "customer-orders-drawer" */ 'pages/customer/orders-drawer'),
    ],
    'customer-address-new-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "customer-address-new-drawer" */ 'pages/customer/address-new-drawer'
        ),
    ],
    'customer-address-edit-drawer': [
      onElementInteraction,
      () =>
        import(
          /* webpackChunkName: "customer-address-edit-drawer" */ 'pages/customer/address-edit-drawer'
        ),
    ],

    collection: [
      intersectionLoader,
      () => import(/* webpackChunkName: "collection-main" */ 'pages/collection/main'),
    ],

    product: [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-main" */ 'pages/product/main'),
    ],
    'product-form': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form'),
    ],
    'product-form-age-confirmation': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-form" */ 'pages/product/form-age-confirmation'),
    ],
    'product-option': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-option" */ 'pages/product/option'),
    ],
    'product-quantity': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-quantity" */ 'pages/product/quantity'),
    ],
    'product-media': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-media" */ 'pages/product/media'),
    ],
    'product-shipping-drawer': [
      onElementInteraction,
      () =>
        import(/* webpackChunkName: "product-shipping-drawer" */ 'pages/product/shipping-drawer'),
    ],
    'product-refund-drawer': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-shipping-drawer" */ 'pages/product/refund-drawer'),
    ],
    'product-shipping': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-shipping" */ 'pages/product/shipping'),
    ],
    'product-shipping-countdown': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-shipping-countdown" */ 'pages/product/shipping-countdown'
        ),
    ],
    'product-certifications': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-certifications" */ 'pages/product/certifications'),
    ],
    'product-availability-drawer': [
      [onElementInteraction, onEvent],
      () =>
        import(
          /* webpackChunkName: "product-availability-drawer" */ 'pages/product/location-selector/main'
        ),
    ],
    'product-back-in-stock': [
      onElementInteraction,
      () => import(/* webpackChunkName: "product-back-in-stock" */ 'pages/product/back-in-stock'),
    ],
    'product-fit-guide': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-fit-guide" */ 'pages/product/product-fit-guide'),
    ],
    'product-sizeguide': [
      intersectionLoader,
      () => import(/* webpackChunkName: "product-sizeguide" */ 'pages/product/sizeguide'),
    ],
    'product-wolt-delivery-drawer': [
      intersectionLoader,
      () =>
        import(
          /* webpackChunkName: "product-wolt-delivery-drawer" */ 'pages/product/product-wolt-delivery-drawer'
        ),
    ],

    'brand-page': [
      intersectionLoader,
      () => import(/* webpackChunkName: "page-brand" */ 'pages/brand/main'),
    ],

    'trending-facets': [
      intersectionLoader,
      () => import(/* webpackChunkName: "page-trending-facets" */ 'pages/404/trending-facets'),
    ],
  });
};
